/**
 * Cleanup for the patient-portal lookup fields (guarantor number and last name).
 *
 * <h3>Why this exists</h3>
 * Patients were being told "Guarantor Number or Last Name is incorrect" for values that were actually
 * correct. The cause was invisible characters riding along with the typed or pasted value: most often a
 * trailing space, but also non-breaking spaces and zero-width characters, which is what you get when
 * someone copies their guarantor number out of a statement PDF, an email, or Word.
 *
 * The verification modal already called `.trim()`, but only to decide whether to ENABLE the Verify
 * button. The value actually sent to the backend was the raw one, so the button lit up and the lookup
 * then failed on a value the patient could see nothing wrong with.
 *
 * <h3>What it deliberately does NOT do</h3>
 * It does not strip punctuation. Real surnames contain apostrophes (O'Brien), hyphens (Smith-Jones) and
 * single internal spaces (Van Lear). Removing those to "sanitise" the field would turn a fixable lookup
 * failure into an unfixable one for those patients, which is a worse bug than the one being fixed.
 *
 * Only characters that cannot legitimately be part of a name or a guarantor number are removed, and
 * internal single spaces are preserved.
 *
 * <p>Every character class below is written with escape sequences ON PURPOSE. An earlier draft held the
 * literal characters and that is unreviewable: an invisible character in source cannot be seen in a
 * diff and can be silently mangled by an editor or a merge.
 */

/**
 * Zero-width and invisible formatting characters, common in text pasted from PDFs and Word.
 * ZWSP, ZWNJ, ZWJ, word joiner, BOM/ZWNBSP, the soft hyphen, and the bidi marks and isolates
 * (LRM/RLM, U+2066-U+2069). All the same category, all the same origin, all the same invisible
 * failure: the value looks perfect on screen and does not match.
 */
const INVISIBLE = /[\u200B\u200C\u200D\u2060\uFEFF\u00AD\u200E\u200F\u2066-\u2069]/g;

/**
 * Characters that look like a space but are not U+0020, normalised so the collapse and trim below
 * can see them. NOTE: JS trim() already strips these at the ends (they count as Unicode whitespace);
 * they are handled here so that an INTERNAL one collapses rather than surviving mid-value.
 */
const EXOTIC_SPACE = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

/** Control characters, including the tabs and newlines a multi-line paste brings with it. */
const CONTROL = /[\u0000-\u001F\u007F]/g;

/**
 * Normalise a value typed or pasted into a lookup field, for sending to the backend.
 *
 * The real gain over a bare trim() is the ZERO-WIDTH characters: they are format characters, not
 * whitespace, so trim() leaves them and the value fails a lookup while looking perfect on screen.
 * Trailing ordinary and non-breaking spaces would have been fixed by trim() alone; the reported bug
 * survived because the submit path never called trim() at all, only the button's enabled check did.
 *
 * Order matters: exotic spaces and control characters become plain spaces FIRST, so the collapse and
 * trim afterwards can see them.
 *
 * Returns '' for null/undefined so callers can treat "nothing usable" uniformly.
 */
export function normaliseLookupInput(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(INVISIBLE, '')
    .replace(EXOTIC_SPACE, ' ')
    .replace(CONTROL, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Choose between what the patient TYPED and what arrived in the query string.
 *
 * <h3>The typed value wins, and that is the whole point</h3>
 * The submit handlers used to read `fromQuery || typed`, so the query value took precedence over an
 * edit the patient had just made. Concrete failure: open `/?lastName=Smtih` with no `gn`. The auto-fire
 * effect needs BOTH params, so it does not run; the GN modal opens instead, pre-filled from the query,
 * with both fields editable. The patient fixes the typo, enters their guarantor number, presses Verify
 * — and "Smtih" is what reaches the API, because the query param is still set. They are told the name
 * is incorrect immediately after correcting it.
 *
 * It is the same defect this module was written to fix, one level up: the modal's enabled-check reads
 * the TYPED state while the submit read the QUERY value, so the two disagreed about what was being
 * verified.
 *
 * <p>Falling back to the query value is still needed: the effect that copies the params into state runs
 * after first paint, so for one render the typed state is empty while the query holds the real value.
 *
 * <p>Both sides are normalised, so a query param of nothing but invisible characters counts as absent
 * rather than beating a genuine typed value.
 */
export function resolveLookupValue(
  typed: string | null | undefined,
  fromQuery: string | null | undefined,
): string {
  const typedClean = normaliseLookupInput(typed);
  return typedClean !== '' ? typedClean : normaliseLookupInput(fromQuery);
}
